import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import { AnimatePresence, motion } from "framer-motion"
import {
  MagnifyingGlass,
  Plus,
  FunnelSimple,
  DotsThreeOutlineVertical,
  PencilSimple,
  Trash,
  UserPlus,
  ArrowClockwise,
  X,
  WarningCircle,
  SpinnerGap,
  Buildings,
  EnvelopeSimple,
  CurrencyDollar,
  Tag,
  PlusCircle,
  ArrowRight,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  CalendarBlank,
  HandPointing,
  CheckCircle,
  EnvelopeOpen,
  Envelope,
} from "phosphor-react"

import { DateRangePicker, SearchableSelect, DeleteModal } from "../../components"
import LeadFormModal from "./LeadFormModal"
import ConvertToClientModal from "./ConvertToClientModal"
import leadsService from "../../services/leadsService"
import { useAccessChecker } from "../../utils/Functions/Authorization"

/* ---------------------------
   tiny utils
--------------------------- */
const normStatus = (status) =>
  (status || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")

const formatStatusLabel = (status) => {
  if (!status) return ""
  return status
    .toString()
    .replace(/[_-]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

const getStatusBadgeColor = (status) => {
  const s = normStatus(status)
  switch (s) {
    case "":
      return "bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400"
    case "incoming":
      return "bg-neutral-100 text-neutral-800 dark:bg-neutral-800/60 dark:text-neutral-200"
    case "unreachable":
      return "bg-cyan-100 text-cyan-800 dark:bg-cyan-800/60 dark:text-cyan-200"
    case "engaged":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400"
    case "pending":
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400"
    case "in negotiation":
      return "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-400"
    case "ready to start":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-400"
    case "onboarded":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400"
    case "declined":
      return "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400"
    default:
      return "bg-neutral-100 text-neutral-800 dark:bg-neutral-900/30 dark:text-neutral-400"
  }
}

const getStatusDotColor = (status) => {
  const s = normStatus(status)
  switch (s) {
    case "":
      return "bg-neutral-500"
    case "incoming":
      return "bg-neutral-400"
    case "unreachable":
      return "bg-cyan-400"
    case "engaged":
      return "bg-blue-500"
    case "pending":
      return "bg-amber-500"
    case "in negotiation":
      return "bg-violet-500"
    case "ready to start":
      return "bg-yellow-500"
    case "onboarded":
      return "bg-emerald-500"
    case "declined":
      return "bg-red-500"
    default:
      return "bg-neutral-500"
  }
}

const isUnreadLead = (lead) => {
  const v =
    lead?.is_read ??
    lead?.isRead ??
    (lead?.read_at ? true : undefined) ??
    (lead?.readAt ? true : undefined)
  if (typeof v === "boolean") return !v
  return false
}

const formatDate = (raw) => {
  if (!raw) return "N/A"
  const dateString = String(raw).trim()
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const dotDateRE =
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/
  const hasExplicitTime =
    /T\d{2}:\d{2}/.test(dateString) || /\s\d{1,2}:\d{2}/.test(dateString)

  let d
  const m = dateString.match(dotDateRE)
  if (m) {
    const [, dd, mm, yyyy, HH, MM] = m
    d = new Date(
      parseInt(yyyy, 10),
      parseInt(mm, 10) - 1,
      parseInt(dd, 10),
      HH ? parseInt(HH, 10) : 0,
      MM ? parseInt(MM, 10) : 0,
      0,
      0,
    )
  } else {
    d = new Date(dateString)
  }
  if (isNaN(d.getTime())) return raw

  const datePart = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone,
  }).format(d)

  if (!hasExplicitTime) return datePart

  const timeFormatter = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  })

  const parts = timeFormatter.formatToParts(d)
  const hour = parts.find((p) => p.type === "hour")?.value ?? "00"
  const minute = parts.find((p) => p.type === "minute")?.value ?? "00"
  const hourNum = parseInt(hour, 10)
  const suffix = hourNum < 12 ? "AM" : "PM"
  const timePart = `${hour}:${minute} ${suffix}`

  return `${datePart}, ${timePart}`
}

/* ---------------------------
   Status Modal (same design)
--------------------------- */
const StatusModal = React.memo(function StatusModal({
  open,
  onClose,
  statusOptions,
  statusLeadId,
  currentStatusValue,
  statusUpdating,
  onPickStatus,
}) {
  const panelRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === "Escape" && onClose()
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose()
    }
    document.addEventListener("keydown", onKey)
    document.addEventListener("mousedown", onClick)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("mousedown", onClick)
    }
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[9999]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/30 dark:bg-black/50" />
          <div className="absolute inset-0 flex items-center justify-center p-3">
            <motion.div
              ref={panelRef}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="w-full max-w-md rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-xl overflow-hidden"
            >
              <div className="px-5 py-4 border-b border-neutral-150 dark:border-neutral-800 flex items-center justify-between">
                <div>
                  <div className="text-md font-[500] text-start text-neutral-900 dark:text-white">
                    Change status
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">
                    Choose a new status for this lead.
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="h-9 w-9 rounded-xl grid place-items-center hover:bg-neutral-100 dark:hover:bg-neutral-900 text-neutral-600 dark:text-neutral-300"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="p-3">
                <div className="grid grid-cols-1 gap-2">
                  {statusOptions
                    .filter((o) => o.value !== "")
                    .map((opt) => {
                      const active =
                        normStatus(opt.value) === normStatus(currentStatusValue)
                      return (
                        <button
                          key={opt.id}
                          disabled={statusUpdating || !statusLeadId}
                          onClick={() => onPickStatus(statusLeadId, opt.value)}
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition
                            ${
                              active
                                ? "border-neutral-900 dark:border-neutral-100 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                                : "border-neutral-150 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900/40 text-neutral-800 dark:text-neutral-200"
                            }`}
                        >
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-[300] ${
                              active
                                ? "bg-white/15 dark:bg-black/10"
                                : getStatusBadgeColor(opt.value)
                            }`}
                          >
                            <span
                              className={`w-2 h-2 mr-1.5 rounded-full ${getStatusDotColor(
                                opt.value,
                              )}`}
                            />
                            {formatStatusLabel(opt.value)}
                          </span>

                          {active ? (
                            <CheckCircle size={18} weight="fill" />
                          ) : (
                            <span className="text-xs">Select</span>
                          )}
                        </button>
                      )
                    })}
                </div>
              </div>

              {statusUpdating && (
                <div className="px-5 py-3 border-t border-neutral-150 dark:border-neutral-800 text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
                  <SpinnerGap
                    size={16}
                    className="animate-[spin_0.8s_linear_infinite]"
                  />
                  Updating status...
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
})

/* ---------------------------
   smart dropdown positioner
--------------------------- */
function clamp(n, min, max) {
  return Math.min(Math.max(n, min), max)
}

function calcMenuPos(rect, menuW, menuH, gap = 8, pad = 8) {
  const vw = window.innerWidth
  const vh = window.innerHeight

  let left = rect.right - menuW
  left = clamp(left, pad, vw - menuW - pad)

  const belowTop = rect.bottom + gap
  const aboveTop = rect.top - gap - menuH

  // prefer below, else above, else clamp inside viewport
  let top = belowTop
  if (belowTop + menuH > vh - pad) {
    if (aboveTop >= pad) top = aboveTop
    else top = clamp(belowTop, pad, vh - menuH - pad)
  }

  return { top, left }
}

const Leads = () => {
  const { checkAccess } = useAccessChecker()

  // ---------------------------
  // data state
  // ---------------------------
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(false)

  // draft filters (UI)
  const [searchQuery, setSearchQuery] = useState("")
  const [dateRange, setDateRange] = useState({ start: "", end: "" })
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [filters, setFilters] = useState({
    platform: "",
    status: "",
    monthlySpend: "",
  })

  // applied query (drives fetching)
  const [queryState, setQueryState] = useState({
    platform: "",
    status: "",
    monthlySpend: "",
    searchValue: "",
    dateRange: { start: "", end: "" },
    pagination: { currentPage: 1, pageSize: 5, totalPages: 1, totalItems: 0 },
    refreshKey: 0,
  })

  const pagination = queryState.pagination

  // ---------------------------
  // modals
  // ---------------------------
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  const [leadFormOpen, setLeadFormOpen] = useState(false)
  const [selectedLeadId, setSelectedLeadId] = useState(null)

  const [convertToClientModal, setConvertToClientModal] = useState(false)

  // unread / read
  const [unreadCount, setUnreadCount] = useState(0)
  const [markingRead, setMarkingRead] = useState(false)

  // status modal
  const [statusModalOpen, setStatusModalOpen] = useState(false)
  const [statusLeadId, setStatusLeadId] = useState(null)
  const [statusUpdating, setStatusUpdating] = useState(false)

  // request guards
  const leadsReqIdRef = useRef(0)
  const unreadReqIdRef = useRef(0)

  // ---------------------------
  // ✅ SMART DROPDOWN (portal)
  // ---------------------------
  const [dropdownOpen, setDropdownOpen] = useState(null) // leadId
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const menuRef = useRef(null)
  const btnRefs = useRef(new Map())
  const rafRef = useRef(null)

  const closeDropdown = useCallback(() => setDropdownOpen(null), [])

  const recomputeDropdown = useCallback(
    (leadId, preferMeasure = true) => {
      const id = leadId ?? dropdownOpen
      if (!id) return

      const btn = btnRefs.current.get(id)
      if (!btn) return

      const rect = btn.getBoundingClientRect()
      const menuW = 192 // w-48
      const menuH = preferMeasure
        ? menuRef.current?.offsetHeight || 220
        : 220

      setDropdownPos(calcMenuPos(rect, menuW, menuH))
    },
    [dropdownOpen],
  )

  const openDropdown = useCallback(
    (leadId) => {
      const btn = btnRefs.current.get(leadId)
      if (!btn) return

      // open first with estimated size (instant)
      setDropdownOpen(leadId)
      const rect = btn.getBoundingClientRect()
      setDropdownPos(calcMenuPos(rect, 192, 220))
    },
    [],
  )

  const toggleDropdown = useCallback(
    async (leadId) => {
      setDropdownOpen((prev) => {
        const next = prev === leadId ? null : leadId
        if (next) openDropdown(leadId)
        return next
      })

      const lead = leads.find((l) => l._id === leadId)
      if (lead && isUnreadLead(lead)) {
        // mark read when opening
        try {
          setMarkingRead(true)
          // optimistic
          setLeads((prev) =>
            prev.map((l) =>
              l._id === leadId
                ? {
                    ...l,
                    is_read: true,
                    isRead: true,
                    read_at: l.read_at || new Date().toISOString(),
                    readAt: l.readAt || new Date().toISOString(),
                  }
                : l,
            ),
          )
          setUnreadCount((c) => Math.max(0, (Number(c) || 0) - 1))
          await leadsService.markAsRead(leadId)
        } catch (e) {
          // rollback best effort
          setQueryState((p) => ({ ...p, refreshKey: p.refreshKey + 1 }))
          console.error("Failed to mark as read:", e)
        } finally {
          setMarkingRead(false)
        }
      }
    },
    [leads, openDropdown],
  )

  // after menu mounts, re-measure real height and reposition (fixes “last rows” bug)
  useLayoutEffect(() => {
    if (!dropdownOpen) return
    const id = requestAnimationFrame(() => recomputeDropdown(dropdownOpen, true))
    return () => cancelAnimationFrame(id)
  }, [dropdownOpen, recomputeDropdown])

  // close / reposition on outside click, esc, scroll, resize
  useEffect(() => {
    if (!dropdownOpen) return

    const onMouseDown = (e) => {
      const menu = menuRef.current
      const btn = btnRefs.current.get(dropdownOpen)

      if (menu && menu.contains(e.target)) return
      if (btn && btn.contains(e.target)) return
      closeDropdown()
    }

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeDropdown()
    }

    const onAnyScroll = () => closeDropdown()

    window.addEventListener("mousedown", onMouseDown)
    window.addEventListener("keydown", onKeyDown)
    window.addEventListener("scroll", onAnyScroll, true)
    window.addEventListener("resize", onAnyScroll)

    return () => {
      window.removeEventListener("mousedown", onMouseDown)
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("scroll", onAnyScroll, true)
      window.removeEventListener("resize", onAnyScroll)
    }
  }, [dropdownOpen, closeDropdown])
  // ---------------------------
  // options
  // ---------------------------
  const platformOptions = useMemo(
    () => [
      { id: 1, name: "All Platforms", value: "" },
      { id: 2, name: "Meta", value: "Meta" },
      { id: 3, name: "Google", value: "Google" },
      { id: 4, name: "TikTok", value: "TikTok" },
      // backend enum has "SnapShat"
      { id: 5, name: "Snapchat", value: "SnapShat" },
    ],
    [],
  )

  const statusOptions = useMemo(
    () => [
      { id: 1, name: "All Statuses", value: "" },
      { id: 2, name: "Incoming", value: "Incoming" },
      { id: 3, name: "Unreachable", value: "Unreachable" },
      { id: 4, name: "Engaged", value: "Engaged" },
      { id: 5, name: "Pending", value: "Pending" },
      { id: 6, name: "In Negotiation", value: "In Negotiation" },
      { id: 7, name: "Ready to Start", value: "Ready to Start" },
      { id: 8, name: "Onboarded", value: "Onboarded" },
      { id: 9, name: "Declined", value: "Declined" },
    ],
    [],
  )

  const monthlySpendOptions = useMemo(
    () => [
      { id: 0, name: "All Spends", value: "" },
      { id: 1, name: "$0 - $50K", value: "$0 - $50K" },
      { id: 2, name: "$50K - $100K", value: "$50K - $100K" },
      { id: 3, name: "$100K - $200K", value: "$100K - $200K" },
      { id: 4, name: "$200K - $300K", value: "$200K - $300K" },
      { id: 5, name: "$300K - $400K", value: "$300K - $400K" },
      { id: 6, name: "$400K - $500K", value: "$400K - $500K" },
      { id: 7, name: "$500K - $1M", value: "$500K - $1M" },
      { id: 8, name: "$1M+", value: "$1M+" },
    ],
    [],
  )

  // ---------------------------
  // derived
  // ---------------------------
  const filtersActive = useMemo(() => {
    return (
      searchQuery !== "" ||
      dateRange.start !== "" ||
      dateRange.end !== "" ||
      filters.platform !== "" ||
      filters.status !== "" ||
      filters.monthlySpend !== ""
    )
  }, [searchQuery, dateRange, filters])

  const unreadOnPageIds = useMemo(
    () => leads.filter(isUnreadLead).map((l) => l._id),
    [leads],
  )

  const currentStatusValue = useMemo(() => {
    if (!statusLeadId) return ""
    const lead = leads.find((l) => l._id === statusLeadId)
    return lead?.status || ""
  }, [statusLeadId, leads])

  // ---------------------------
  // platform info (same UI)
  // ---------------------------
  const getPlatformInfo = useCallback((platform) => {
    const p = platform === "SnapShat" ? "Snapchat" : platform
    switch (p) {
      case "Meta":
        return {
          name: "Meta",
          icon: (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 48 48"
            >
              <path
                fill="#0081fb"
                d="M47,29.36l-2.193,1.663L42.62,29.5c0-0.16,0-0.33-0.01-0.5c0-0.16,0-0.33-0.01-0.5
                c-0.14-3.94-1.14-8.16-3.14-11.25c-1.54-2.37-3.51-3.5-5.71-3.5c-2.31,0-4.19,1.38-6.27,4.38c-0.06,0.09-0.13,0.18-0.19,0.28
                c-0.04,0.05-0.07,0.1-0.11,0.16c-0.1,0.15-0.2,0.3-0.3,0.46c-0.9,1.4-1.84,3.03-2.86,4.83c-0.09,0.17-0.19,0.34-0.28,0.51
                c-0.03,0.04-0.06,0.09-0.08,0.13l-0.21,0.37l-1.24,2.19c-2.91,5.15-3.65,6.33-5.1,8.26C14.56,38.71,12.38,40,9.51,40
                c-3.4,0-5.56-1.47-6.89-3.69C1.53,34.51,1,32.14,1,29.44l4.97,0.17c0,1.76,0.38,3.1,0.89,3.92C7.52,34.59,8.49,35,9.5,35
                c1.29,0,2.49-0.27,4.77-3.43c1.83-2.53,3.99-6.07,5.44-8.3l1.37-2.09l0.29-0.46l0.3-0.45l0.5-0.77c0.76-1.16,1.58-2.39,2.46-3.57
                c0.1-0.14,0.2-0.28,0.31-0.42c0.1-0.14,0.21-0.28,0.31-0.41c0.9-1.15,1.85-2.22,2.87-3.1c1.85-1.61,3.84-2.5,5.85-2.5
                c3.37,0,6.58,1.95,9.04,5.61c2.51,3.74,3.82,8.4,3.97,13.25c0.01,0.16,0.01,0.33,0.01,0.5C47,29.03,47,29.19,47,29.36z"
              ></path>
              <linearGradient
                id="meta_g1"
                x1="42.304"
                x2="13.533"
                y1="24.75"
                y2="24.75"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor="#0081fb"></stop>
                <stop offset=".995" stopColor="#0064e1"></stop>
              </linearGradient>
              <path
                fill="url(#meta_g1)"
                d="M4.918,15.456
                C7.195,11.951,10.483,9.5,14.253,9.5c2.184,0,4.354,0.645,6.621,2.493c2.479,2.02,5.122,5.346,8.419,10.828l1.182,1.967
                c2.854,4.746,4.477,7.187,5.428,8.339C37.125,34.606,37.888,35,39,35c2.82,0,3.617-2.54,3.617-5.501L47,29.362
                c0,3.095-0.611,5.369-1.651,7.165C44.345,38.264,42.387,40,39.093,40c-2.048,0-3.862-0.444-5.868-2.333
                c-1.542-1.45-3.345-4.026-4.732-6.341l-4.126-6.879c-2.07-3.452-3.969-6.027-5.068-7.192c-1.182-1.254-2.642-2.754-5.067-2.754
                c-1.963,0-3.689,1.362-5.084,3.465L4.918,15.456z"
              ></path>
            </svg>
          ),
        }
      case "Google":
        return {
          name: "Google",
          icon: (
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
          ),
        }
      case "TikTok":
        return {
          name: "TikTok",
          icon: (
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
            </svg>
          ),
        }
      case "Snapchat":
        return { name: "Snapchat", icon: <Tag size={16} /> }
      default:
        return { name: platform || "Unknown", icon: <Tag size={16} /> }
    }
  }, [])

  // ---------------------------
  // page title
  // ---------------------------
  useEffect(() => {
    const event = new CustomEvent("setPageTitle", {
      detail: { title: "Leads Management" },
    })
    window.dispatchEvent(event)
    return () => {
      const resetEvent = new CustomEvent("setPageTitle", {
        detail: { title: "Dashboard" },
      })
      window.dispatchEvent(resetEvent)
    }
  }, [])

  // ---------------------------
  // API
  // ---------------------------
  const fetchUnreadCount = useCallback(async () => {
    const reqId = ++unreadReqIdRef.current
    try {
      const res = await leadsService.unreadCount()
      if (reqId !== unreadReqIdRef.current) return
      const count = res?.data?.data?.count ?? res?.data?.count ?? 0
      setUnreadCount(Number(count) || 0)
    } catch (e) {
      console.error("Failed to fetch unread count:", e)
    }
  }, [])

  const fetchLeads = useCallback(async () => {
    const reqId = ++leadsReqIdRef.current
    setLoading(true)

    try {
      const payload = {
        platform: queryState.platform,
        status: queryState.status,
        monthlySpend: queryState.monthlySpend,
        searchValue: queryState.searchValue,
        dateRange: queryState.dateRange,
        pagination: {
          page: queryState.pagination.currentPage,
          limit: queryState.pagination.pageSize,
        },
      }

      const response = await leadsService.search(payload)
      if (reqId !== leadsReqIdRef.current) return

      const responseData = response.data
      if (responseData?.statusCode === 200) {
        const list = responseData.data || []
        setLeads(list)

        if (responseData.pagination) {
          setQueryState((prev) => ({
            ...prev,
            pagination: {
              ...prev.pagination,
              currentPage:
                responseData.pagination.currentPage ?? prev.pagination.currentPage,
              pageSize:
                responseData.pagination.pageSize ?? prev.pagination.pageSize,
              totalPages: responseData.pagination.totalPages ?? 1,
              totalItems: responseData.pagination.totalItems ?? 0,
            },
          }))
        }
      }
    } catch (error) {
      console.error("Failed to fetch leads:", error)
    } finally {
      if (reqId === leadsReqIdRef.current) setLoading(false)
    }
  }, [
    queryState.platform,
    queryState.status,
    queryState.monthlySpend,
    queryState.searchValue,
    queryState.dateRange,
    queryState.pagination.currentPage,
    queryState.pagination.pageSize,
    queryState.refreshKey,
  ])

  useEffect(() => {
    fetchLeads()
    fetchUnreadCount()
  }, [fetchLeads, fetchUnreadCount])

  // ---------------------------
  // local patches read/unread
  // ---------------------------
  const patchLocalRead = useCallback((leadId, readValue = true) => {
    setLeads((prev) =>
      prev.map((l) => {
        if (l._id !== leadId) return l
        if (readValue) {
          const stamp = new Date().toISOString()
          return {
            ...l,
            is_read: true,
            isRead: true,
            read_at: l.read_at || stamp,
            readAt: l.readAt || stamp,
          }
        }
        return { ...l, is_read: false, isRead: false, read_at: null, readAt: null }
      }),
    )
  }, [])

  const markAsRead = useCallback(
    async (leadId) => {
      if (!leadId) return
      const lead = leads.find((l) => l._id === leadId)
      const wasUnread = lead ? isUnreadLead(lead) : false
      if (!wasUnread) return

      try {
        setMarkingRead(true)
        patchLocalRead(leadId, true)
        setUnreadCount((c) => Math.max(0, (Number(c) || 0) - 1))
        await leadsService.markAsRead(leadId)
      } catch (e) {
        patchLocalRead(leadId, false)
        fetchUnreadCount()
        console.error("Failed to mark as read:", e)
      } finally {
        setMarkingRead(false)
      }
    },
    [leads, patchLocalRead, fetchUnreadCount],
  )

  const markAsUnread = useCallback(
    async (leadId) => {
      if (!leadId) return
      const lead = leads.find((l) => l._id === leadId)
      if (!lead) return

      const isIncoming = normStatus(lead.status) === "incoming"
      if (!isIncoming) return

      const currentlyUnread = isUnreadLead(lead)
      if (currentlyUnread) return

      try {
        setMarkingRead(true)
        patchLocalRead(leadId, false)
        setUnreadCount((c) => (Number(c) || 0) + 1)
        await leadsService.markAsUnread(leadId)
      } catch (e) {
        patchLocalRead(leadId, true)
        fetchUnreadCount()
        console.error("Failed to mark as unread:", e)
      } finally {
        setMarkingRead(false)
      }
    },
    [leads, patchLocalRead, fetchUnreadCount],
  )

  const markManyAsRead = useCallback(
    async (ids) => {
      const list = Array.isArray(ids) ? ids.filter(Boolean) : []
      if (!list.length) return

      const unreadSet = new Set(leads.filter(isUnreadLead).map((l) => l._id))
      const willMark = list.filter((id) => unreadSet.has(id))

      try {
        setMarkingRead(true)
        if (willMark.length) {
          setLeads((prev) =>
            prev.map((l) => (willMark.includes(l._id) ? { ...l, is_read: true, isRead: true } : l)),
          )
          setUnreadCount((c) => Math.max(0, (Number(c) || 0) - willMark.length))
        }
        await leadsService.markManyAsRead(list)
      } catch (e) {
        console.error("Failed to mark many as read:", e)
        fetchUnreadCount()
        setQueryState((p) => ({ ...p, refreshKey: p.refreshKey + 1 }))
      } finally {
        setMarkingRead(false)
      }
    },
    [leads, fetchUnreadCount],
  )

  const markAllAsRead = useCallback(async () => {
    if ((Number(unreadCount) || 0) === 0) return
    try {
      setMarkingRead(true)
      setLeads((prev) => prev.map((l) => ({ ...l, is_read: true, isRead: true })))
      setUnreadCount(0)
      await leadsService.markAllAsRead()
    } catch (e) {
      console.error("Failed to mark all as read:", e)
      fetchUnreadCount()
      setQueryState((p) => ({ ...p, refreshKey: p.refreshKey + 1 }))
    } finally {
      setMarkingRead(false)
    }
  }, [unreadCount, fetchUnreadCount])

  // ---------------------------
  // status update
  // ---------------------------
  const updateStatus = useCallback(
    async (leadId, newStatus) => {
      if (!leadId || !newStatus) return
      const lead = leads.find((l) => l._id === leadId)
      const wasUnread = lead ? isUnreadLead(lead) : false

      try {
        setStatusUpdating(true)

        setLeads((prev) =>
          prev.map((l) =>
            l._id === leadId
              ? {
                  ...l,
                  status: newStatus,
                  is_read: true,
                  isRead: true,
                  read_at: l.read_at || new Date().toISOString(),
                  readAt: l.readAt || new Date().toISOString(),
                }
              : l,
          ),
        )
        if (wasUnread) setUnreadCount((c) => Math.max(0, (Number(c) || 0) - 1))

        await leadsService.updateStatus(leadId, {
          status: newStatus,
          markRead: true,
        })

        setStatusModalOpen(false)
        setStatusLeadId(null)
      } catch (e) {
        console.error("Failed to update status:", e)
        fetchUnreadCount()
        setQueryState((p) => ({ ...p, refreshKey: p.refreshKey + 1 }))
      } finally {
        setStatusUpdating(false)
      }
    },
    [leads, fetchUnreadCount],
  )

  // ---------------------------
  // filters/pagination actions
  // ---------------------------
  const handleFilterChange = useCallback((filterName, value) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters({ platform: "", status: "", monthlySpend: "" })
    setSearchQuery("")
    setDateRange({ start: "", end: "" })
  }, [])

  const applyFilters = useCallback(() => {
    closeDropdown()
    setQueryState((prev) => ({
      ...prev,
      platform: filters.platform,
      status: filters.status,
      monthlySpend: filters.monthlySpend,
      searchValue: searchQuery,
      dateRange,
      pagination: { ...prev.pagination, currentPage: 1 },
      refreshKey: prev.refreshKey + 1,
    }))
  }, [filters, searchQuery, dateRange, closeDropdown])

  const resetAndRefresh = useCallback(() => {
    closeDropdown()
    clearFilters()
    setQueryState((prev) => ({
      ...prev,
      platform: "",
      status: "",
      monthlySpend: "",
      searchValue: "",
      dateRange: { start: "", end: "" },
      pagination: { ...prev.pagination, currentPage: 1 },
      refreshKey: prev.refreshKey + 1,
    }))
  }, [clearFilters, closeDropdown])

  const handleSearch = useCallback(() => {
    closeDropdown()
    setQueryState((prev) => ({
      ...prev,
      platform: filters.platform,
      status: filters.status,
      monthlySpend: filters.monthlySpend,
      searchValue: searchQuery,
      dateRange,
      pagination: { ...prev.pagination, currentPage: 1 },
      refreshKey: prev.refreshKey + 1,
    }))
  }, [filters, searchQuery, dateRange, closeDropdown])

  const handlePageChange = useCallback((newPage) => {
    closeDropdown()
    setQueryState((prev) => {
      if (newPage < 1 || newPage > prev.pagination.totalPages) return prev
      return { ...prev, pagination: { ...prev.pagination, currentPage: newPage } }
    })
  }, [closeDropdown])

  const handlePageSizeChange = useCallback((e) => {
    closeDropdown()
    const newSize = Number.parseInt(e.target.value, 10)
    setQueryState((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, pageSize: newSize, currentPage: 1 },
      refreshKey: prev.refreshKey + 1,
    }))
  }, [closeDropdown])

  const getPageNumbers = useCallback(() => {
    const totalPages = pagination.totalPages
    const currentPage = pagination.currentPage
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1)
    if (currentPage <= 3) return [1, 2, 3, 4, "...", totalPages]
    if (currentPage >= totalPages - 2)
      return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages]
  }, [pagination.totalPages, pagination.currentPage])

  // ---------------------------
  // other actions
  // ---------------------------
  const openLeadForm = useCallback(
    async (id = null) => {
      closeDropdown()
      setSelectedLeadId(id)
      setLeadFormOpen(true)

      if (id) {
        const lead = leads.find((l) => l._id === id)
        if (lead && isUnreadLead(lead)) await markAsRead(id)
      }
    },
    [leads, markAsRead, closeDropdown],
  )

  const openConvertToClient = useCallback(
    async (id) => {
      closeDropdown()
      setSelectedLeadId(id)
      setConvertToClientModal(true)

      const lead = leads.find((l) => l._id === id)
      if (lead && isUnreadLead(lead)) await markAsRead(id)
    },
    [leads, markAsRead, closeDropdown],
  )

  const openStatusModal = useCallback(
    async (leadId) => {
      closeDropdown()
      setStatusLeadId(leadId)
      setStatusModalOpen(true)

      const lead = leads.find((l) => l._id === leadId)
      if (lead && isUnreadLead(lead)) await markAsRead(leadId)
    },
    [leads, markAsRead, closeDropdown],
  )

  const openWhatsapp = useCallback((phone) => {
    const toE164 = (raw) => {
      let num = String(raw || "").replace(/\D/g, "")
      if (num.startsWith("0")) num = "212" + num.slice(1)
      return num
    }

    const rawMessage = `*Greetings 👋,*

*Thank you for filling out the onboarding form. We’re reaching out to confirm your details and guide you through the next steps.*

*Please reply when you’re available*`

    const encoded = encodeURIComponent(rawMessage)
    const url = `https://wa.me/${toE164(phone)}?text=${encoded}`
    window.open(url, "_blank", "noopener,noreferrer")
  }, [])

  // ---------------------------
  // animations (same feel)
  // ---------------------------
  const pageAnim = useMemo(
    () => ({
      initial: { opacity: 0, y: 14 },
      animate: { opacity: 1, y: 0 },
      exit: { opacity: 0, y: -10 },
      transition: { duration: 0.22 },
    }),
    [],
  )

  const rowAnim = useMemo(
    () => ({
      hidden: { opacity: 0, y: 8 },
      show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.18, delay: Math.min(i * 0.02, 0.18) },
      }),
    }),
    [],
  )

  // ---------------------------
  // render
  // ---------------------------
  return (
    <motion.div {...pageAnim} className="lg:p-7 p-3 max-w-full mx-auto">
      {/* Top bar */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-grow max-w-xs">
          <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
            <MagnifyingGlass size={16} className="text-neutral-800 dark:text-neutral-400" />
          </div>
          <input
            type="text"
            placeholder="Search leads..."
            className="h-10 w-full border pl-10 pr-10 text-sm border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl placeholder-neutral-500 ring-neutral-300 dark:ring-neutral-700 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200 focus:outline-none focus:ring-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
            onClick={handleSearch}
            aria-label="Submit search"
          >
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Date */}
        <DateRangePicker
          onRangeChange={(range) => setDateRange(range)}
          value={dateRange}
          className={
            dateRange.start || dateRange.end
              ? "border-blue-500 bg-blue-50 text-blue-600 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-400"
              : ""
          }
        />

        {/* More filters */}
        <button
          className={`flex items-center ring-yellow-500/50 gap-2 px-4 py-2.5 h-10 w-fit border text-sm rounded-xl cursor-pointer focus:outline-none focus:ring-1 ${
            showMoreFilters
              ? "border-yellow-500/50 bg-yellow-50 text-yellow-600 dark:border-yellow-600/30 dark:bg-yellow-900/20 dark:text-yellow-400"
              : "border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 bg-white dark:bg-neutral-950 text-neutral-600 dark:text-neutral-200"
          }`}
          onClick={() => setShowMoreFilters((v) => !v)}
        >
          <span className="text-sm">{showMoreFilters ? "Hide filters" : "More filters"}</span>
          {showMoreFilters ? (
            <X size={16} className="text-yellow-600 dark:text-yellow-400" />
          ) : (
            <PlusCircle size={16} className="text-neutral-800 dark:text-neutral-400" />
          )}
        </button>

        {/* Reset */}
        <button
          className="flex items-center gap-2 h-10 px-4 border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
          onClick={resetAndRefresh}
          aria-label="Reset filters"
        >
          <ArrowClockwise size={16} className="text-neutral-800 dark:text-neutral-400" />
          <span className="text-sm">Reset</span>
        </button>

        {/* Unread badge + mark all read */}
        <div className="flex items-center gap-2">
          <div className="h-10 px-3 rounded-xl border border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-950 flex items-center gap-2">
            <span className="text-sm text-neutral-700 dark:text-neutral-200">Unread</span>
            <span className="min-w-[26px] h-6 px-2 rounded-full bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black text-xs grid place-items-center">
              {unreadCount}
            </span>
          </div>

          <button
            onClick={markAllAsRead}
            disabled={markingRead || unreadCount === 0}
            className={`h-10 px-4 rounded-xl border text-sm flex items-center gap-2 transition
              ${
                markingRead || unreadCount === 0
                  ? "border-neutral-200 dark:border-neutral-800 text-neutral-400 dark:text-neutral-600 cursor-not-allowed bg-white dark:bg-neutral-950"
                  : "border-neutral-150 dark:border-neutral-900 bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800"
              }`}
          >
            {markingRead ? (
              <SpinnerGap size={16} className="animate-[spin_0.8s_linear_infinite]" />
            ) : (
              <EnvelopeOpen size={16} />
            )}
            Mark all read
          </button>
        </div>

        {/* Apply Filters (only when filters active & not expanded) */}
        {filtersActive && !showMoreFilters && (
          <button
            className="flex items-center text-xs justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400"
            onClick={applyFilters}
          >
            <FunnelSimple size={16} />
            <span className="text-xs">Apply Filters</span>
          </button>
        )}

        {/* Create */}
        {checkAccess("Leads", "create") && (
          <button
            className="flex items-center gap-2 px-4 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 ml-auto"
            onClick={() => openLeadForm()}
          >
            <Plus size={16} />
            <span className="text-sm">Add New Lead</span>
          </button>
        )}
      </div>

      {/* Active filters chips */}
      {filtersActive && (
        <div className="mb-4 flex flex-wrap gap-2 items-center">
          <span className="text-sm text-neutral-500 dark:text-neutral-400">Active filters:</span>

          {searchQuery && (
            <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
              <MagnifyingGlass size={12} />
              <span>{searchQuery}</span>
              <button
                onClick={() => {
                  setSearchQuery("")
                  handleSearch()
                }}
                className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {(dateRange.start || dateRange.end) && (
            <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
              <CalendarBlank size={12} />
              <span>
                {dateRange.start ? new Date(dateRange.start).toLocaleDateString() : "Any"} -{" "}
                {dateRange.end ? new Date(dateRange.end).toLocaleDateString() : "Any"}
              </span>
              <button
                onClick={() => {
                  setDateRange({ start: "", end: "" })
                  handleSearch()
                }}
                className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {filters.platform && (
            <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
              <Tag size={12} />
              <span>{filters.platform === "SnapShat" ? "Snapchat" : filters.platform}</span>
              <button
                onClick={() => {
                  handleFilterChange("platform", "")
                  applyFilters()
                }}
                className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {filters.status && (
            <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
              <WarningCircle size={12} />
              <span>{filters.status}</span>
              <button
                onClick={() => {
                  handleFilterChange("status", "")
                  applyFilters()
                }}
                className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {filters.monthlySpend && (
            <div className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 px-2 py-1 rounded-full text-xs">
              <CurrencyDollar size={12} />
              <span>{filters.monthlySpend}</span>
              <button
                onClick={() => {
                  handleFilterChange("monthlySpend", "")
                  applyFilters()
                }}
                className="ml-1 hover:text-blue-900 dark:hover:text-blue-100"
              >
                <X size={12} />
              </button>
            </div>
          )}

          <button
            onClick={resetAndRefresh}
            className="text-xs text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1 ml-2"
          >
            <X size={12} />
            <span>Clear all</span>
          </button>
        </div>
      )}

      {/* More filters panel */}
      <AnimatePresence>
        {showMoreFilters && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="mb-6 p-6 border border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950/80"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-[300] text-neutral-800 dark:text-white">More filters</h2>
              <button
                onClick={clearFilters}
                className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 flex items-center gap-1.5"
              >
                <X size={14} />
                <span>Clear all</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <SearchableSelect
                options={platformOptions}
                value={filters.platform}
                onChange={(value) => handleFilterChange("platform", value)}
                placeholder="Platform"
                icon={<Tag size={16} />}
              />

              <SearchableSelect
                options={statusOptions}
                value={filters.status}
                onChange={(value) => handleFilterChange("status", value)}
                placeholder="Status"
                icon={<WarningCircle size={16} />}
              />

              <SearchableSelect
                options={monthlySpendOptions}
                value={filters.monthlySpend}
                onChange={(value) => handleFilterChange("monthlySpend", value)}
                placeholder="Monthly Spend"
                icon={<CurrencyDollar size={16} />}
              />

              <div className="flex items-center gap-2">
                <button
                  className="flex items-center text-sm justify-center px-4 gap-2 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400"
                  onClick={applyFilters}
                >
                  <FunnelSimple size={16} />
                  <span className="text-xs">Apply Filters</span>
                </button>

                <button
                  className="flex items-center text-sm justify-center px-4 gap-2 h-10 border border-neutral-150 dark:border-neutral-900 rounded-xl bg-white dark:bg-neutral-950 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-800 transition"
                  onClick={() => markManyAsRead(unreadOnPageIds)}
                  disabled={markingRead || unreadOnPageIds.length === 0}
                >
                  {markingRead ? (
                    <SpinnerGap size={16} className="animate-[spin_0.8s_linear_infinite]" />
                  ) : (
                    <EnvelopeOpen size={16} />
                  )}
                  Mark page read
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Table card */}
      <div className="border border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30 rounded-lg bg-white dark:bg-neutral-900/30">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <SpinnerGap
              size={30}
              className="animate-[spin_0.8s_linear_infinite] text-neutral-600 dark:text-neutral-400 drop-shadow-sm"
            />
          </div>
        ) : leads.length > 0 ? (
          <>
            <div className="overflow-x-auto rounded-t-lg">
              <table className="min-w-full divide-y divide-neutral-150 dark:divide-neutral-800">
                <thead className="bg-neutral-50 h-14 whitespace-nowrap dark:border-neutral-900 dark:bg-neutral-900/30">
                  <tr>
                    <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Join Date
                    </th>
                    <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Lead
                    </th>
                    <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Whatsapp
                    </th>
                    <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Monthly Spend
                    </th>
                    <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Read
                    </th>
                    <th className="px-6 py-4 text-start text-xs font-[300] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white font-[300] dark:bg-neutral-950 divide-y divide-neutral-150 dark:divide-neutral-800">
                  {leads.map((lead, idx) => {
                    const platformInfo = getPlatformInfo(lead.platform)
                    const unread = isUnreadLead(lead)
                    const isIncoming = normStatus(lead.status) === "incoming"

                    return (
                      <motion.tr
                        key={lead._id}
                        custom={idx}
                        variants={rowAnim}
                        initial="hidden"
                        animate="show"
                        className={`hover:bg-neutral-50 text-start dark:hover:bg-neutral-800/50 ${
                          unread ? "bg-blue-50/40 dark:bg-blue-900/10" : ""
                        }`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-800 dark:text-white">
                          <span className="font-[400] text-neutral-800 dark:text-neutral-100">
                            {formatDate(lead.joinDate || lead.createdAt)}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-[300] text-neutral-800 dark:text-white">
                            {lead.name}
                          </div>
                          <div className="text-[13px] lowercase text-neutral-500 dark:text-neutral-400 flex items-center gap-1">
                            <EnvelopeSimple size={12} />
                            <span>{lead.email}</span>
                          </div>
                          <div className="text-[13px] lowercase text-neutral-500 dark:text-neutral-400 flex items-center gap-1">
                            <Buildings size={12} />
                            <span>{lead.company}</span>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-800 dark:text-white">
                          <div
                            onClick={() => openWhatsapp(lead.phone)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") openWhatsapp(lead.phone)
                            }}
                            className="flex justify-center text-[13px] items-center rounded-full gap-1 bg-green-600/10 text-green-600 px-3 py-1 cursor-pointer whitespace-nowrap text-sm hover:bg-green-600/15 transition-colors"
                            aria-label="Open WhatsApp chat"
                          >
                            <HandPointing size={14} className="rotate-6" />
                            {lead.phone}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="mr-2">{platformInfo.icon}</div>
                            <span className="text-sm text-neutral-800 dark:text-white">
                              {platformInfo.name}
                            </span>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-800 dark:text-white">
                          {lead.monthlySpend}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-800 dark:text-white">
                          {lead.category}
                        </td>

                        {/* status */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => openStatusModal(lead._id)}
                            className={`inline-flex items-center px-4 py-1.5 rounded-full text-xs font-[300] hover:scale-[1.02] transition ${getStatusBadgeColor(
                              lead.status,
                            )}`}
                          >
                            <span
                              className={`w-2 h-2 mr-1.5 rounded-full ${getStatusDotColor(lead.status)}`}
                            />
                            {formatStatusLabel(lead.status)}
                          </button>
                        </td>

                        {/* read */}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => {
                              if (unread) return markAsRead(lead._id)
                              if (!unread && isIncoming) return markAsUnread(lead._id)
                              return null
                            }}
                            disabled={(!unread && !isIncoming) || markingRead}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition
                              ${
                                unread
                                  ? "border-blue-300 bg-blue-100 text-blue-700 dark:border-blue-800/60 dark:bg-blue-900/30 dark:text-blue-300 hover:scale-[1.02]"
                                  : isIncoming
                                  ? "border-neutral-200 bg-white text-neutral-700 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-300 hover:scale-[1.02]"
                                  : "border-neutral-200 bg-white text-neutral-500 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-400 cursor-not-allowed"
                              }`}
                          >
                            {unread ? <Envelope size={14} /> : <EnvelopeOpen size={14} />}
                            {unread ? "Unread" : "Read"}
                          </button>
                        </td>

                        {/* actions */}
                        <td className="px-6 py-4 whitespace-nowrap text-start text-sm font-[300]">
                          <button
                            ref={(node) => {
                              if (!node) btnRefs.current.delete(lead._id)
                              else btnRefs.current.set(lead._id, node)
                            }}
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleDropdown(lead._id)
                            }}
                            className="p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-400"
                            aria-label="Actions"
                            type="button"
                          >
                            <DotsThreeOutlineVertical size={16} weight="bold" />
                          </button>
                        </td>
                      </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 flex flex-col sm:flex-row items-center justify-between border-t border-neutral-150 dark:border-neutral-900 dark:bg-neutral-900/30">
              <div className="flex items-center mb-4 sm:mb-0">
                <span className="text-sm text-neutral-600 dark:text-neutral-400">
                  Showing {leads.length} of {pagination.totalItems} leads
                </span>

                <div className="ml-4">
                  <select
                    value={pagination.pageSize}
                    onChange={handlePageSizeChange}
                    className="h-8 px-2 text-xs border border-neutral-200 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-white"
                  >
                    <option value={5}>5 / page</option>
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                    <option value={50}>50 per page</option>
                    <option value={100}>100 per page</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.currentPage === 1}
                  className={`p-1.5 rounded-xl ${
                    pagination.currentPage === 1
                      ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
                      : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  }`}
                  aria-label="First page"
                >
                  <CaretDoubleLeft size={16} />
                </button>

                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className={`p-1.5 rounded-xl ${
                    pagination.currentPage === 1
                      ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
                      : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  }`}
                  aria-label="Previous page"
                >
                  <CaretLeft size={16} />
                </button>

                <div className="flex items-center space-x-1">
                  {getPageNumbers().map((page, index) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-2 py-1 text-neutral-500 dark:text-neutral-400"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-8 h-8 flex items-center justify-center rounded-xl text-sm ${
                          pagination.currentPage === page
                            ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black"
                            : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`p-1.5 rounded-xl ${
                    pagination.currentPage === pagination.totalPages
                      ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
                      : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  }`}
                  aria-label="Next page"
                >
                  <CaretRight size={16} />
                </button>

                <button
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`p-1.5 rounded-xl ${
                    pagination.currentPage === pagination.totalPages
                      ? "text-neutral-400 dark:text-neutral-600 cursor-not-allowed"
                      : "text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  }`}
                  aria-label="Last page"
                >
                  <CaretDoubleRight size={16} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col text-sm items-center justify-center h-64 p-8">
            <div className="p-2 rounded-full bg-neutral-100 dark:bg-neutral-900/20 flex items-center justify-center mb-4">
              <WarningCircle size={24} className="text-neutral-600 dark:text-neutral-400" />
            </div>
            <h3 className="text-xl font-medium text-neutral-900 dark:text-white mb-2">No Leads Found</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center max-w-md mb-6">
              {filtersActive
                ? "No leads match your search criteria. Try adjusting your filters or search term."
                : "There are no leads in the system yet. Add your first lead to get started."}
            </p>
            <div className="flex gap-4">
              <button
                onClick={resetAndRefresh}
                className="px-4 py-2 border border-neutral-200 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-white hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors flex items-center gap-2"
              >
                <ArrowClockwise size={16} />
                Reset Filters
              </button>

              {checkAccess("Leads", "create") && (
                <button
                  onClick={() => openLeadForm()}
                  className="px-4 py-2 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 flex items-center gap-2"
                >
                  <Plus size={16} />
                  <span>Add Lead</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ✅ ONE dropdown portal (NOT per-row) */}
      {createPortal(
        <AnimatePresence>
          {dropdownOpen ? (
            <motion.div
              ref={menuRef}
              className="fixed w-48 bg-white dark:bg-neutral-900/60 backdrop-blur-md rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-900 p-1 z-[9998]"
              style={{ top: dropdownPos.top, left: dropdownPos.left }}
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.14 }}
              onClick={(e) => e.stopPropagation()}
            >
              {checkAccess("Leads", "update") && (
                <button
                  onClick={() => openLeadForm(dropdownOpen)}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl"
                >
                  <PencilSimple size={16} className="mr-2" />
                  Edit Lead
                </button>
              )}

              <button
                onClick={() => openConvertToClient(dropdownOpen)}
                className="flex items-center w-full text-left px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl"
              >
                <UserPlus size={16} className="mr-2" />
                Convert to Client
              </button>

              {checkAccess("Leads", "delete") && (
                <button
                  onClick={() => {
                    setDeleteId(dropdownOpen)
                    setDeleteModal(true)
                    closeDropdown()
                  }}
                  className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl"
                >
                  <Trash size={16} className="mr-2" />
                  Delete
                </button>
              )}
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.body,
      )}

      {/* Delete Confirmation Modal */}
      <DeleteModal
        setDeleteId={setDeleteId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={deleteId}
        Model="leads"
        setDone={() => setQueryState((p) => ({ ...p, refreshKey: p.refreshKey + 1 }))}
        setLoding={setLoading}
        done={queryState.refreshKey}
      />

      {/* Lead Form Modal */}
      <LeadFormModal
        isOpen={leadFormOpen}
        onClose={() => setLeadFormOpen(false)}
        leadId={selectedLeadId}
        onSuccess={() => setQueryState((p) => ({ ...p, refreshKey: p.refreshKey + 1 }))}
      />

      {/* Convert to Client Modal */}
      <ConvertToClientModal
        isOpen={convertToClientModal}
        onClose={() => setConvertToClientModal(false)}
        leadId={selectedLeadId}
        onSuccess={() => setQueryState((p) => ({ ...p, refreshKey: p.refreshKey + 1 }))}
      />

      {/* Status Modal */}
      <StatusModal
        open={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false)
          setStatusLeadId(null)
        }}
        statusOptions={statusOptions}
        statusLeadId={statusLeadId}
        currentStatusValue={currentStatusValue}
        statusUpdating={statusUpdating}
        onPickStatus={updateStatus}
      />
    </motion.div>
  )
}

export default Leads
